// @genesys-custom-file
'use client';

import { c as _c } from "react/compiler-runtime";
import { CopyCheckIcon, CopyIcon } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from './button';
import type { buttonVariants } from './button';
import type { VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
interface CopyButtonProps extends React.ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  value: string;
  src?: string;
  /** When set, shows icon + label (e.g. "Copy error") with text size; defaults to `iconToggle` + `sm`. */
  label?: string;
  toast?: string | boolean;
  showToastContent?: boolean;
  /** Tooltip on hover; when `label` is set, defaults to "Click to ..." / "Copied!". */
  tooltip?: React.ReactNode;
}
export async function copyToClipboardWithMeta(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
}
export function CopyButton(t0) {
  const $ = _c(37);
  let className;
  let label;
  let props;
  let size;
  let t1;
  let t2;
  let tooltip;
  let value;
  let variant;
  if ($[0] !== t0) {
    const {
      value: t3,
      label: t4,
      className: t5,
      src: _unusedSrc,
      variant: t6,
      size: t7,
      toast: t8,
      showToastContent: t9,
      tooltip: t10,
      ...t11
    } = t0;
    value = t3;
    label = t4;
    className = t5;
    variant = t6;
    size = t7;
    t1 = t8;
    t2 = t9;
    tooltip = t10;
    props = t11;
    $[0] = t0;
    $[1] = className;
    $[2] = label;
    $[3] = props;
    $[4] = size;
    $[5] = t1;
    $[6] = t2;
    $[7] = tooltip;
    $[8] = value;
    $[9] = variant;
  } else {
    className = $[1];
    label = $[2];
    props = $[3];
    size = $[4];
    t1 = $[5];
    t2 = $[6];
    tooltip = $[7];
    value = $[8];
    variant = $[9];
  }
  const toastProp = t1 === undefined ? true : t1;
  const showToastContent = t2 === undefined ? false : t2;
  const [hasCopied, setHasCopied] = React.useState(false);
  const labeled = Boolean(label);
  const resolvedVariant = variant ?? (labeled ? "iconToggle" : "ghost");
  const resolvedSize = size ?? (labeled ? "sm" : "icon");
  let t3;
  if ($[10] !== hasCopied || $[11] !== label || $[12] !== labeled || $[13] !== tooltip) {
    t3 = tooltip ?? (labeled ? hasCopied ? "Copied!" : `Click to ${label.toLowerCase()}` : undefined);
    $[10] = hasCopied;
    $[11] = label;
    $[12] = labeled;
    $[13] = tooltip;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const resolvedTooltip = t3;
  let t4;
  let t5;
  if ($[15] !== hasCopied) {
    t4 = () => {
      if (hasCopied) {
        const timer = setTimeout(() => {
          setHasCopied(false);
        }, 2000);
        return () => clearTimeout(timer);
      }
    };
    t5 = [hasCopied];
    $[15] = hasCopied;
    $[16] = t4;
    $[17] = t5;
  } else {
    t4 = $[16];
    t5 = $[17];
  }
  React.useEffect(t4, t5);
  const t6 = labeled && "gap-1";
  let t7;
  if ($[18] !== className || $[19] !== t6) {
    t7 = cn(t6, className);
    $[18] = className;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== showToastContent || $[22] !== toastProp || $[23] !== value) {
    t8 = async e => {
      e.preventDefault();
      e.stopPropagation();
      const success = await copyToClipboardWithMeta(value);
      if (success) {
        setHasCopied(true);
        if (toastProp) {
          if (typeof toastProp === "string") {
            toast.info(toastProp, {
              description: showToastContent ? value : undefined,
              duration: 2000
            });
          } else {
            toast.info("Copied to clipboard", {
              description: showToastContent ? value : undefined,
              duration: 2000
            });
          }
        }
      } else {
        if (toastProp) {
          toast.error("Failed to copy to clipboard", {
            duration: 3000
          });
        }
      }
    };
    $[21] = showToastContent;
    $[22] = toastProp;
    $[23] = value;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== hasCopied || $[26] !== label || $[27] !== labeled) {
    t9 = labeled ? <>{hasCopied ? <CopyCheckIcon className="size-4 shrink-0" /> : <CopyIcon className="size-4 shrink-0" />}<span className="text-xs">{label}</span></> : <><span className="sr-only">Copy</span>{hasCopied ? <CopyCheckIcon className="size-4" /> : <CopyIcon className="size-4" />}</>;
    $[25] = hasCopied;
    $[26] = label;
    $[27] = labeled;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== props || $[30] !== resolvedSize || $[31] !== resolvedTooltip || $[32] !== resolvedVariant || $[33] !== t7 || $[34] !== t8 || $[35] !== t9) {
    t10 = <Button type="button" {...props} size={resolvedSize} variant={resolvedVariant} className={t7} tooltip={resolvedTooltip} tooltipSide="bottom" onClick={t8}>{t9}</Button>;
    $[29] = props;
    $[30] = resolvedSize;
    $[31] = resolvedTooltip;
    $[32] = resolvedVariant;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  return t10;
}